import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { buildInfo } from '../../build-info';
import { InfoDialogComponent } from '../components/shared/info-dialog/info-dialog.component';

interface VersionManifest {
  version: string;
  commit?: string;
  buildDate?: string;
}

/**
 * Detects when a newer frontend has been deployed while this SPA is still open,
 * so the user can be pushed onto the current version before a stale write can
 * corrupt data. Works together with the server-side guard (ClientVersionMiddleware
 * + client-version.interceptor): this service is the proactive heads-up, the 426
 * path is the hard stop.
 *
 * The deployed version is published at /version.json (served uncached by nginx,
 * generated by set-build-info.js); we compare it to the version baked into this
 * build (build-info.ts).
 */
@Injectable({ providedIn: 'root' })
export class UpdateCheckService {
  private readonly http = inject(HttpClient);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);

  /** Set once a newer frontend is detected (poll) or enforced by the backend (HTTP 426). */
  public readonly updateAvailable = signal(false);

  private readonly pollIntervalMs = 60_000;
  private started = false;
  private reloadDialogOpen = false;

  /** The version this SPA was built from. */
  private readonly currentVersion = buildInfo.version;

  /** Dev builds carry the 0.0.0-dev sentinel — never nag during local development. */
  private get enabled(): boolean {
    return !!this.currentVersion && !this.currentVersion.includes('0.0.0-dev');
  }

  /** Begin watching for new deployments. Safe to call once at startup. */
  public start(): void {
    if (!this.enabled || this.started) {
      return;
    }
    this.started = true;

    // Check periodically, when the tab regains focus, and on each navigation —
    // whichever happens first surfaces the banner.
    setInterval(() => this.checkNow(), this.pollIntervalMs);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.checkNow();
      }
    });
    window.addEventListener('focus', () => this.checkNow());
    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe(() => this.checkNow());

    this.checkNow();
  }

  /** Fetch the deployed manifest and flip the signal if it differs from this build. */
  public checkNow(): void {
    if (!this.enabled || this.updateAvailable()) {
      return;
    }
    // Cache-bust defensively in case an intermediary ignores the no-cache header.
    this.http.get<VersionManifest>(`/version.json?_=${Date.now()}`).subscribe({
      next: manifest => {
        if (manifest?.version && manifest.version !== this.currentVersion) {
          this.updateAvailable.set(true);
        }
      },
      error: () => {
        /* transient/offline — ignore and retry on the next tick */
      }
    });
  }

  /**
   * Called when the backend rejects a write from this (stale) client with 426.
   * Surfaces the banner and a blocking, non-dismissable reload prompt.
   */
  public forceUpdateRequired(): void {
    this.updateAvailable.set(true);
    this.promptReload();
  }

  private promptReload(): void {
    if (this.reloadDialogOpen) {
      return;
    }
    this.reloadDialogOpen = true;
    this.dialog
      .open(InfoDialogComponent, {
        width: '440px',
        disableClose: true,
        data: {
          title: 'New version required',
          message:
            'This page is running an older version of the application and can no longer ' +
            'save changes. Reload to get the latest version and continue.',
          icon: 'system_update',
          iconColor: 'warn',
          buttonText: 'Reload now',
          buttonIcon: 'refresh'
        }
      })
      .afterClosed()
      .subscribe(() => window.location.reload());
  }
}
